import * as R from 'ramda';

import { NativeQueriesService } from '../../../../../../shared/NativeQueries.service';
import AndFilter, { type AndType } from './filters/AndFilter';
import type { InType } from './filters/InFilter';
import type { LikeType } from './filters/LikeFilter';
import type LikeFilter from './filters/LikeFilter';
import type { NotType } from './filters/NotFilter';
import type { OrType } from './filters/OrFilter';

export const IN_TYPE: InType = 'in';
export const LIKE_TYPE: LikeType = 'like';
export const NOT_TYPE: NotType = 'not';
export const AND_TYPE: AndType = 'and';
export const OR_TYPE: OrType = 'or';

export const OPERATORS = {
  EQUALS: 'equals',
  DOES_NOT_EQUAL: 'does not equal',
  CONTAINS: 'contains',
  DOES_NOT_CONTAIN: 'does not contain',
  STARTS_WITH: 'starts with',
  DOES_NOT_START_WITH: 'does not start with',
  ENDS_WITH: 'ends with',
  DOES_NOT_END_WITH: 'does not end with',
};

// [EG-3109] only show equals and does not equal operators for fraud_names and fraud_codes filters
export const RESTRICTED_OPERATORS = {
  EQUALS: 'equals',
  DOES_NOT_EQUAL: 'does not equal',
};

export type FilterDisplayFormat = {
  dimension: string;
  comparator: string;
  values: string[];
  required?: boolean;
  nestingLevel?: number;
  combiner?: string;
};

/**
 * Currently merging is supported only for a single entry on mergeFilter
 * @param  {[type]} sourceFilter [description]
 * @param  {[type]} mergeFilter  limitation of only 1 dimension and value
 * @return {[type]}              [description]
 */
export function mergeFilters(sourceFilter, mergeFilter, implicitRequiredFilter = false) {
  if (mergeFilter && mergeFilter.type) {
    if (sourceFilter.type === AND_TYPE) {
      const mergedFilters = sourceFilter.fields.filter((field) => field.dimension === mergeFilter.dimension);
      if (mergedFilters.length === 0) {
        return new AndFilter([mergeFilter, sourceFilter]);
      } else if (
        mergedFilters[0].values &&
        mergedFilters[0].values.length !== 0 &&
        mergedFilters[0].values[0] !== mergeFilter.values[0]
      ) {
        if (implicitRequiredFilter) {
          return new AndFilter([mergeFilter, sourceFilter]);
        } else {
          return {
            ...sourceFilter,
            fields: sourceFilter.fields.map((field) => (field.dimension === mergeFilter.dimension ? mergeFilter : field)),
          };
        }
      } else if (mergedFilters[0].values && mergedFilters[0].values.length === 0) {
        return new AndFilter([mergeFilter, sourceFilter]);
      }
    } else if (!sourceFilter.type) {
      return new AndFilter([mergeFilter]);
    } else {
      return new AndFilter([mergeFilter, sourceFilter]);
    }
  }
  return sourceFilter;
}

export function convertMultipleQueriesToNativeDruidFilters(sourceQueries) {
  return (
    sourceQueries &&
    Object.keys(sourceQueries).reduce((acc, source) => {
      acc[source] = new NativeQueriesService().convertFiltersToNativeDruidFilters(sourceQueries[source]);
      return acc;
    }, {})
  );
}

export function isRequiredFilter(dimension, requiredFilter) {
  return !!(requiredFilter && dimension === requiredFilter.dimension);
  // return !!(requiredFilters && requiredFilters.filter(requiredFilter => {
  //   return requiredFilter.dimension === dimension;
  // }).length > 0);
}

export function getComparatorAndValueForLike(filter: LikeFilter, operator?) {
  if (!filter.pattern) {
    return {};
  }
  if (filter.pattern.charAt(0) === '%') {
    if (filter.pattern.slice(-1) === '%') {
      return {
        comparator: operator === NOT_TYPE ? OPERATORS.DOES_NOT_CONTAIN : OPERATORS.CONTAINS,
        value: filter.pattern.slice(1, -1),
      };
    } else {
      return {
        comparator: operator === NOT_TYPE ? OPERATORS.DOES_NOT_END_WITH : OPERATORS.ENDS_WITH,
        value: filter.pattern.slice(1),
      };
    }
  } else if (filter.pattern.slice(-1) === '%') {
    return {
      comparator: operator === NOT_TYPE ? OPERATORS.DOES_NOT_START_WITH : OPERATORS.STARTS_WITH,
      value: filter.pattern.slice(0, -1),
    };
  }
  return {
    value: filter.pattern,
  };
}

export function convertOrToDisplayFormat(filter, requiredFilters, nestingLevel, combiner, operator) {
  if (
    filter.fields.reduce((acc, field, i) => {
      if (i === 0) {
        return {
          dimension: field.dimension,
          isLike: field.type === LIKE_TYPE,
        };
      }
      if ((acc.isLike === null || acc.isLike === true) && acc.dimension === field.dimension) {
        return {
          dimension: acc.dimension,
          isLike: true,
        };
      } else {
        return {
          dimension: acc.dimension,
          isLike: false,
        };
      }
    }, {}).isLike
  ) {
    const required = isRequiredFilter(filter.fields[0].dimension, requiredFilters);
    const comparator = filter.comparator;
    return filter.fields.reduce((acc, fieldFilter) => {
      const comparatorAndValue = getComparatorAndValueForLike(fieldFilter, operator);
      if (acc[0]) {
        acc[0].values.push(comparatorAndValue.value);
        return acc;
      }
      acc.push({
        dimension: fieldFilter.dimension,
        values: [comparatorAndValue.value],
        comparator: comparator || comparatorAndValue.comparator,
        required,
        nestingLevel: nestingLevel || 0,
        combiner,
      });
      return acc;
    }, []);
  }
  return filter.fields.reduce((acc, fieldFilter) => {
    return acc.concat(
      convertNativeFiltersToDisplayFormat(
        fieldFilter,
        requiredFilters,
        Number.isInteger(nestingLevel) ? nestingLevel + 1 : 0,
        OR_TYPE
      )
    );
  }, []);
}

/**
 * Converts native druid query to display format
 * DruidFilter: http://druid.io/docs/latest/querying/filters.html
 * DisplayFormat:
 * {
 *    dimension: key,
 *    comparator: 'equals',
 *    values: val,
 *    required,
 *    nestingLevel,
 *    combiner
 * }
 * @param  {DruidFilter} filter
 * @param  {DruidFilter} requiredFilters a single native filter
 * @param  {number} nestingLevel    Used for determining nesting level
 * @param  {string} combiner        AND | OR
 * @param  {string} operator        One of the 8 OPERATORS
 * @return {Array<DisplayFormat>}
 */
export function convertNativeFiltersToDisplayFormat(filter, requiredFilters?, nestingLevel?, combiner?, operator?) {
  if (filter) {
    switch (filter.type) {
      case OR_TYPE:
        return convertOrToDisplayFormat(filter, requiredFilters, nestingLevel, combiner, operator);
      case AND_TYPE:
        return filter.fields.reduce((acc, fieldFilter) => {
          return acc.concat(
            convertNativeFiltersToDisplayFormat(
              fieldFilter,
              requiredFilters,
              Number.isInteger(nestingLevel) ? nestingLevel + 1 : 0,
              AND_TYPE
            )
          );
        }, []);
      case NOT_TYPE:
        return convertNativeFiltersToDisplayFormat(filter.field, requiredFilters, nestingLevel, combiner, NOT_TYPE);
      case IN_TYPE:
        return [
          {
            dimension: filter.dimension,
            values: filter.values,
            comparator: operator === NOT_TYPE ? OPERATORS.DOES_NOT_EQUAL : OPERATORS.EQUALS,
            required: isRequiredFilter(filter.dimension, requiredFilters),
            nestingLevel: nestingLevel || 0,
            combiner,
          },
        ];
      case LIKE_TYPE:
        const comparatorAndValue = getComparatorAndValueForLike(filter, operator);
        return [
          {
            dimension: filter.dimension,
            values: [comparatorAndValue.value],
            comparator: comparatorAndValue.comparator,
            required: isRequiredFilter(filter.dimension, requiredFilters),
            nestingLevel: nestingLevel || 0,
            combiner,
          },
        ];
    }
  } else {
    return [];
  }
}

/**
 * Pick out all dimensions and values associated with that dimension
 *   for the given filter
 * @param  {DruidFilter} filter
 * @return {Object { <dimension> : Array<value> }}
 */
export function getListOfDimensionsAndValues(filter) {
  if (!filter || !filter.type) {
    return {};
  }
  switch (filter.type) {
    case OR_TYPE:
    case AND_TYPE:
      return filter.fields
        .map((field) => getListOfDimensionsAndValues(field))
        .reduce((acc, dimension) => {
          return R.mergeWith(
            (a, b) => {
              return R.uniq(a.concat(b));
            },
            acc,
            dimension
          );
        }, {});
    case NOT_TYPE:
      return getListOfDimensionsAndValues(filter.field);
    case IN_TYPE:
      return {
        [filter.dimension]: filter.values,
      };
    case LIKE_TYPE:
      const value = getComparatorAndValueForLike(filter);
      return {
        [filter.dimension]: [value.value],
      };
    default:
      return {};
  }
}

export const convertShareableStateFiltersFromLegacyToNative = (shareableState, nestingLevel) => {
  if (!shareableState) {
    return;
  }

  if (!nestingLevel) {
    return shareableState;
  }

  if (shareableState.query) {
    const nextShareableState = R.assoc(
      'query',
      new NativeQueriesService().convertFiltersToNativeDruidFilters(shareableState.query),
      shareableState
    );
    delete nextShareableState.query.filters;
    return nextShareableState;
  }

  // iterate over shareable state keys and recurse
  return Object.keys(shareableState).reduce((acc, key) => {
    if (Object.prototype.toString.call(shareableState[key]) !== '[object Object]') {
      return R.assoc(key, shareableState[key], acc);
    }

    return R.assoc(key, convertShareableStateFiltersFromLegacyToNative(shareableState[key], nestingLevel - 1), acc);
  }, {});
};

export const getLikeFilterComparatorAndValues = (likeFilter, isNot) => {
  if (!likeFilter.pattern) {
    return {};
  }
  if (likeFilter.pattern.charAt(0) === '%') {
    if (likeFilter.pattern.slice(-1) === '%') {
      return {
        values: [likeFilter.pattern.slice(1, -1)],
        comparator: isNot ? OPERATORS.DOES_NOT_CONTAIN : OPERATORS.CONTAINS,
      };
    } else {
      return {
        values: [likeFilter.pattern.slice(1)],
        comparator: isNot ? OPERATORS.DOES_NOT_END_WITH : OPERATORS.ENDS_WITH,
      };
    }
  } else if (likeFilter.pattern.slice(-1) === '%') {
    return {
      values: [likeFilter.pattern.slice(0, -1)],
      comparator: isNot ? OPERATORS.DOES_NOT_START_WITH : OPERATORS.STARTS_WITH,
    };
  }
  return {
    values: [likeFilter.pattern],
    comparator: isNot ? OPERATORS.DOES_NOT_CONTAIN : OPERATORS.CONTAINS,
  };
};

// returns an object where the key is the dimension, and the value is an object with the (1) the comparator and (2) the values
export const getListOfDimensionsValuesAndComparators = (filter, isNot) => {
  switch (filter.type) {
    case OR_TYPE:
    case AND_TYPE:
      return filter.fields
        .map((field) => getListOfDimensionsValuesAndComparators(field, isNot))
        .reduce((acc, dimension) => {
          return R.mergeWith(
            (a, b) => {
              return {
                comparator: a.comparator || b.comparator,
                values: R.uniq(a.values.concat(b.values)),
              };
            },
            acc,
            dimension
          );
        }, {});
    case NOT_TYPE:
      return getListOfDimensionsValuesAndComparators(filter.field, true);
    case IN_TYPE:
      return {
        [filter.dimension]: {
          comparator: isNot ? OPERATORS.DOES_NOT_EQUAL : OPERATORS.EQUALS,
          values: filter.values,
        },
      };
    case LIKE_TYPE:
      return {
        [filter.dimension]: getLikeFilterComparatorAndValues(filter, isNot),
      };
  }
};
